import { useLayoutEffect, useRef } from 'react';

import { withoutStopLine } from './copyLint';
import {
  FIELD_LABEL,
  FIELD_LIMITS,
  interpolate,
  straightenQuotes,
  SAMPLE_VALUES,
  SampleValue,
} from './copyModel';
import { gsm7, smsLength, smsSegments } from './smsText';
import { Caption, Chip, Label, Locked, TextArea } from './styles';
import Pill from '../shared/Pill';

import { CopyLocale } from '@/services/lifecycleAPI';

type Props = {
  field: string;
  value: string;
  changed: boolean;
  allowed: string[];
  issues: string[];
  readOnly: boolean;
  curlyFixed: boolean;
  longestValues: Record<string, SampleValue>;
  stopLine: string;
  stopLineAdded: boolean;
  locale: CopyLocale;
  onChange: (raw: string) => void;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const countLine = (
  field: string,
  value: string,
  longestValues: Record<string, SampleValue>,
  stopLine: string,
  locale: CopyLocale,
): { text: string; warn: boolean } => {
  const longest = interpolate(value, longestValues);
  if (field === 'sms') {
    const sample = withoutStopLine(interpolate(value, SAMPLE_VALUES), stopLine);
    const long = withoutStopLine(longest, stopLine);
    const ok = gsm7(long);
    if (locale === 'es') {
      const segments = smsSegments(long);
      return {
        text:
          `Sample values: ${plural(smsLength(sample), 'character')}, ${plural(smsSegments(sample), 'segment')}` +
          ` · Longest values: ${plural(smsLength(long), 'character')}, ${plural(segments, 'segment')}` +
          ` · ${ok ? 'GSM-7' : 'UCS-2 (70 characters per segment)'} · Limit 2 segments`,
        warn: segments > 2,
      };
    }
    return {
      text:
        `Sample values: ${plural(smsLength(sample), 'character')}, ${plural(smsSegments(sample), 'segment')}` +
        ` · Longest values: ${plural(smsLength(long), 'character')}, ${plural(smsSegments(long), 'segment')}` +
        ` · ${ok ? 'GSM-7 OK' : 'Not GSM-7 (70 characters per segment)'}`,
      warn: !ok || smsLength(long) > 160,
    };
  }
  if (field === 'body') {
    const words = longest.split(/\s+/).filter(Boolean).length;
    return {
      text: `${words} words with the longest values (under 120)`,
      warn: words >= 120,
    };
  }
  const limit = FIELD_LIMITS[field];
  return {
    text: `${plural(longest.length, 'character')} with the longest values${limit ? ` (limit ${limit})` : ''}`,
    warn: limit !== undefined && longest.length > limit,
  };
};

const rowsFor = (field: string): number => {
  if (field === 'body') return 8;
  return field === 'sms' || field === 'push_body' ? 3 : 1;
};

const FieldEditor = ({
  field,
  value,
  changed,
  allowed,
  issues,
  readOnly,
  curlyFixed,
  longestValues,
  stopLine,
  stopLineAdded,
  locale,
  onChange,
}: Props) => {
  const area = useRef<HTMLTextAreaElement>(null);
  const touched = useRef(false);
  const pendingCursor = useRef<number | null>(null);
  const count = countLine(field, value, longestValues, stopLine, locale);

  useLayoutEffect(() => {
    if (pendingCursor.current === null || !area.current) return;
    const position = pendingCursor.current;
    pendingCursor.current = null;
    area.current.focus();
    area.current.setSelectionRange(position, position);
  }, [value]);

  const insert = (name: string) => {
    const token = `%{${name}}`;
    const node = area.current;
    const start = touched.current && node ? node.selectionStart : value.length;
    const end = touched.current && node ? node.selectionEnd : value.length;
    pendingCursor.current = start + token.length;
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);
  };

  const handleChange = (raw: string, caret: number) => {
    pendingCursor.current = straightenQuotes(raw) === value ? null : caret;
    onChange(raw);
  };

  return (
    <div
      data-testid={`copyFieldBox-${field}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <Label>{FIELD_LABEL[field] ?? field}</Label>
          {changed && <Pill tone="blue">Changed</Pill>}
        </span>
        <TextArea
          ref={area}
          data-testid={`copyField-${field}`}
          rows={rowsFor(field)}
          value={value}
          readOnly={readOnly}
          invalid={issues.length > 0}
          title={readOnly ? 'Tenant admin only' : undefined}
          onFocus={() => {
            touched.current = true;
          }}
          onChange={(e) =>
            handleChange(e.target.value, e.target.selectionStart ?? 0)
          }
        />
      </label>
      <Caption
        data-testid={`copyCount-${field}`}
        style={count.warn ? { color: '#B0030A', fontWeight: 600 } : undefined}
      >
        {count.text}
      </Caption>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
        }}
      >
        <Caption>Insert:</Caption>
        {allowed.map((name) => (
          <Chip
            key={name}
            type="button"
            disabled={readOnly}
            onClick={() => insert(name)}
          >
            {`%{${name}}`}
          </Chip>
        ))}
        {allowed.length === 0 && <Caption>no fields for this text</Caption>}
      </div>
      {curlyFixed && (
        <Caption style={{ color: '#7A5600' }}>
          Curly quotes were changed to straight ones (a curly quote makes an SMS
          cost more).
        </Caption>
      )}
      {issues.map((message) => (
        <span
          key={message}
          role="alert"
          style={{ color: '#B0030A', fontSize: 13, fontWeight: 600 }}
        >
          {message}
        </span>
      ))}
      {field === 'sms' && stopLineAdded && (
        <Locked>
          {`Added by the engine to a worker's first SMS: "${stopLine}" Not editable.`}
        </Locked>
      )}
    </div>
  );
};

export default FieldEditor;
